import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type { AgendarVisitaRequestValidated, CrearVisitaRequestValidated } from '../schemas/visita.schema';
import { deduplicarAdjuntosVisita } from '../utils/visita-adjuntos.util';
import { normalizarCamposRegistroEnVisita, resolverGenerarFolioVisita } from '../utils/visita-registro.util';
import { resolverDocumentoResumenVisita } from '../utils/visita-resumen-firmado-adjunto.util';

/**
 * Interfaz para una jerarquía según la especificación OpenAPI
 */
export interface Jerarquia {
  descripcion: string;
}

/**
 * Interfaz para un productor según la especificación OpenAPI
 */
export interface Productor {
  id_productor: string;  // UUID
  nombre: string;
  /** Primer apellido (API devuelve nombre, primer_apellido, segundo_apellido por separado) */
  primer_apellido?: string;
  /** Segundo apellido */
  segundo_apellido?: string;
  /** RUT del productor (usuario INDAP) */
  rut?: string | null;
  comuna: string | null;
  email: string | null;
  ultima_visita: string | null;
  coordenadas: {
    lat: number;
    lng: number;
  } | null;
  jerarquia: Jerarquia;
}

/** Metadato del resumen firmado (listado `registro_visita.documento_resumen` o detalle en raíz). */
export interface DocumentoResumenVisitaApi {
  mime_type?: string | null;
  fecha_subida?: string | null;
  tamano_bytes?: number | null;
  nombre_archivo?: string | null;
  ruta_almacenamiento?: string | null;
  url_descarga?: string | null;
}

/**
 * Interfaz para un adjunto de visita según la especificación OpenAPI (GET /visita/{id_visita})
 */
export interface AdjuntoItem {
  mime_type: string;
  fecha_subida: string;
  id_categoria: number;
  tamano_bytes: number;
  nombre_archivo: string;
  ruta_almacenamiento: string;
  url_descarga?: string | null;
  categoria_nombre?: string | null;
}

/**
 * Registro de visita anidado en listados (planificada, finalizada, etc.).
 */
export interface RegistroVisita {
  descripcion?: string | null;
  fecha_inicio_planificada?: string | null;
  fecha_termino_planificada?: string | null;
  folio?: string | null;
  lugar?: string | null;
  adjuntos?: AdjuntoItem[];
  comentarios?: string | null;
  compromisos?: string | null;
  hora_inicio?: string | null;
  hora_termino?: string | null;
  fecha_visita?: string | null;
  tipo_reporte?: string | null;
  recomendaciones?: string | null;
  usuario_registro?: string | null;
  id_objetivo_visita?: number | number[] | null;
  id_modalidad_contacto?: number | null;
  modalidad_contacto?: string | null;
  /** Textos de objetivos si el API los devuelve como lista. */
  objetivos?: string[] | null;
  objetivo?: string | string[] | null;
  objetivo_otro?: string | null;
  desarrollo_asesoria?: string | null;
  /** true cuando el registro de visita quedó guardado parcialmente en servidor. */
  es_parcial?: boolean | null;
  /** Si true, el backend asigna el folio al registrar (OpenAPI POST /registrar). */
  generar_folio?: boolean | null;
  /** Resumen firmado subido por el extensionista (listado). */
  documento_resumen?: DocumentoResumenVisitaApi | null;
}

/**
 * Consultor/usuario asociado a una visita (OpenAPI: ConsultorResponse).
 */
export interface ConsultorResponse {
  id_usuario: string; // UUID
  nombre: string;
  email?: string | null;
  rut?: string | null;
}

/**
 * Interfaz para una visita individual según la especificación OpenAPI
 * Incluye campos calculados (latitud, longitud) y campos de detalle (GET /visita/{id_visita})
 */
export interface Visita {
  id_visita: string;  // UUID
  id_usuario?: string;  // Opcional; el endpoint de detalle no lo devuelve
  usuario: string;
  fecha: string;  // ISO date-time
  fecha_termino?: string;  // ISO date-time
  lugar: string;
  estado: string;
  tipo: string;
  productor: Productor;
  // Campos opcionales que pueden venir en los detalles (GET /visita/{id_visita})
  comentarios?: string;
  es_contingencia?: boolean;
  bolsa_visita?: boolean;
  descripcion_emergencia?: string | null;
  consultor?: ConsultorResponse | null;
  motivo?: string;
  /** Nombre del consultor que registró la visita (campo nuevo). */
  usuario_registro?: string | null;
  adjuntos?: AdjuntoItem[];
  /** Fecha de realización de la visita (formato fecha) */
  fecha_visita?: string | null;
  /** Hora de inicio de la visita */
  hora_inicio?: string | null;
  /** Hora de término de la visita */
  hora_termino?: string | null;
  /** Desarrollo de la asesoría */
  desarrollo_asesoria?: string | null;
  /** Recomendaciones realizadas */
  recomendaciones?: string | null;
  /** Compromisos acordados */
  compromisos?: string | null;
  /** Tipo de reporte al registrar (ej. digital, papel) */
  tipo_reporte?: string | null;
  /**
   * Objetivo(s) de registro: en GET detalle OpenAPI es `string[]`; en listados legacy puede ser texto.
   */
  objetivo?: string | string[] | null;
  /** Alias normalizado en cliente (textos de objetivos). */
  objetivos?: string[] | null;
  /** Texto detallado cuando se elige "Otro" en objetivos. */
  objetivo_otro?: string | null;
  /** Modalidad de contacto al registrar (id). */
  id_modalidad_contacto?: number | null;
  /** Descripción de modalidad de contacto (detalle GET). */
  modalidad_contacto?: string | null;
  /** Folio de respaldo */
  folio?: string | null;
  /** Descripción de la visita (planificación o detalle; distinta de descripcion_emergencia). */
  descripcion?: string | null;
  /** Inicio planificado (estado Planificada). */
  fecha_inicio_planificada?: string | null;
  /** Término planificado (estado Planificada). */
  fecha_termino_planificada?: string | null;
  /** Objeto anidado del listado/detalle API. */
  registro_visita?: RegistroVisita | null;
  /** UUID de solución vinculada (plan de trabajo); null si no aplica. */
  id_solucion?: string | null;
  /** ID(s) del objetivo de visita (listado / registro parcial). */
  id_objetivo_visita?: number | number[] | null;
  /** true cuando hay un borrador de registro guardado parcialmente. */
  es_parcial?: boolean | null;
  /** Si true, el backend asigna el folio al registrar. */
  generar_folio?: boolean | null;
  /** Resumen firmado subido por el extensionista (detalle GET). */
  documento_resumen?: DocumentoResumenVisitaApi | null;
  // Campos calculados para compatibilidad con código existente
  latitud?: number;
  longitud?: number;
}

/**
 * Interfaz para un estado de visita
 */
export interface EstadoVisita {
  id_estado_visita: number;
  descripcion: string;
}

/**
 * Interfaz para un tipo de visita
 */
export interface TipoVisita {
  id_tipo_visita: number;
  descripcion: string;
}

/**
 * Interfaz para una categoría de anexo (adjunto) según OpenAPI GET /api/v1/visita/obtenerCategoriasAnexo
 */
export interface CategoriaAnexoResponse {
  id_categoria_anexo: number;
  descripcion: string;
}

/**
 * Objetivo de visita (catálogo). OpenAPI: GET /api/v1/visita/obtenerObjetivosVisita
 */
export interface ObjetivoVisita {
  id_objetivo_visita: number;
  descripcion: string;
}

/**
 * Modalidad de contacto (catálogo). OpenAPI: GET /api/v1/visita/obtenerModalidadContacto
 */
export interface ModalidadContacto {
  id_modalidad_contacto: number;
  descripcion: string;
}

/** Etiqueta UI para visitas agendadas con registro parcial guardado en servidor. */
export const ETIQUETA_ESTADO_GUARDADO_PARCIAL = 'Agendada (Guardado Parcial)';

/** Sufijo del valor ion-select para filtrar Agendada + es_parcial=true. */
export const SUFIJO_FILTRO_ESTADO_PARCIAL = '-parcial';

/**
 * Parámetros de consulta para obtener visitas según la especificación OpenAPI
 */
export interface VisitasQueryParams {
  desde?: string;       // Formato: yyyy-mm-dd
  hasta?: string;       // Formato: yyyy-mm-dd
  id_estado_visita?: number;  // Cambiado de 'estado' a 'id_estado_visita' según OpenAPI
  id_tipo_visita?: number;    // Filtro por tipo de visita
  es_parcial?: boolean;       // Filtro por registro parcial (OpenAPI query param)
  page?: number;        // Default: 1, minimum: 1
  size?: number;        // Default: 5, minimum: 1
  sort?: string;        // Default: "fecha"
  order?: 'asc' | 'desc';  // Default: "asc"
  search?: string;
}

/**
 * Interfaz para la respuesta de la API (listado, sin campos calculados)
 */
interface VisitaResponse {
  id_visita: string;
  id_usuario: string;
  usuario: string;
  fecha: string;
  lugar: string;
  estado: string;
  tipo: string;
  productor: Productor;
  consultor?: ConsultorResponse | null;
  usuario_registro?: string | null;
  comentarios?: string;
  es_contingencia?: boolean;
  bolsa_visita?: boolean;
  descripcion_emergencia?: string | null;
  registro_visita?: RegistroVisita | null;
  descripcion?: string | null;
  id_solucion?: string | null;
  tipo_reporte?: string | null;
  id_objetivo_visita?: number | number[] | null;
  id_modalidad_contacto?: number | null;
  modalidad_contacto?: string | null;
  objetivos?: string[] | null;
  objetivo_otro?: string | null;
  folio?: string | null;
  es_parcial?: boolean | null;
  generar_folio?: boolean | null;
}

/**
 * Respuesta del endpoint GET /api/v1/visita/{id_visita} (detalle de visita).
 * Alineado con VisitaDetalleResponse del OpenAPI (incluye es_parcial y campos de registro en raíz).
 */
interface VisitaDetalleResponse {
  id_visita: string;
  consultor?: ConsultorResponse | null;
  fecha: string;
  lugar: string;
  estado: string;
  tipo: string;
  motivo?: string | null;
  es_contingencia?: boolean;
  bolsa_visita?: boolean;
  generar_folio?: boolean;
  /** UUID de solución vinculada (plan de trabajo); null si no aplica. */
  id_solucion?: string | null;
  es_parcial?: boolean;
  fecha_visita?: string | null;
  hora_inicio?: string | null;
  hora_termino?: string | null;
  desarrollo_asesoria?: string | null;
  recomendaciones?: string | null;
  compromisos?: string | null;
  adjuntos?: AdjuntoItem[];
  productor: Productor;
  descripcion_emergencia?: string | null;
  descripcion?: string | null;
  tipo_reporte?: string | null;
  /** Objetivo(s) de registro (OpenAPI GET detalle: array de strings). */
  objetivo?: string[] | null;
  /** IDs de los objetivos de visita (para fallback con catálogo). */
  id_objetivo_visita?: number[] | null;
  objetivo_otro?: string | null;
  modalidad_contacto?: string | null;
  folio?: string | null;
  usuario_registro?: string | null;
  /** Puede venir en GET aunque no esté documentado en OpenAPI. */
  documento_resumen?: DocumentoResumenVisitaApi | null;
  registro_visita?: RegistroVisita | null;
}

/** Aplica campos de registro_visita y coordenadas al modelo Visita. */
function mapVisitaFromApiResponse(
  visitaResp: VisitaResponse | VisitaDetalleResponse
): Visita {
  const reg =
    'registro_visita' in visitaResp ? visitaResp.registro_visita ?? null : null;
  const coordenadas = visitaResp.productor?.coordenadas;
  const latitud = coordenadas?.lat;
  const longitud = coordenadas?.lng;

  const descripcionRaiz =
    'descripcion' in visitaResp && visitaResp.descripcion != null
      ? visitaResp.descripcion
      : null;
  const descripcion =
    (descripcionRaiz && String(descripcionRaiz).trim()) ||
    (reg?.descripcion && String(reg.descripcion).trim()) ||
    null;

  const respConParcial = visitaResp as VisitaResponse & { es_parcial?: boolean | null };
  const es_parcial = respConParcial.es_parcial ?? reg?.es_parcial ?? null;

  const visita: Visita = {
    ...(visitaResp as VisitaResponse),
    es_contingencia: Boolean(visitaResp.es_contingencia),
    id_solucion: visitaResp.id_solucion ?? null,
    registro_visita: reg,
    descripcion,
    fecha_inicio_planificada: reg?.fecha_inicio_planificada ?? null,
    fecha_termino_planificada: reg?.fecha_termino_planificada ?? null,
    es_parcial,
  };

  if (latitud !== null && latitud !== undefined && isFinite(latitud)) {
    visita.latitud = latitud;
  }
  if (longitud !== null && longitud !== undefined && isFinite(longitud)) {
    visita.longitud = longitud;
  }

  return normalizarCamposRegistroEnVisita(visita);
}

/**
 * Descripción/objetivo de planificación desde el modelo Visita (raíz, registro_visita o motivo legacy).
 */
export function obtenerDescripcionVisita(visita?: Visita | null): string {
  if (!visita) return '';
  return (
    visita.descripcion?.trim() ||
    visita.registro_visita?.descripcion?.trim() ||
    visita.motivo?.trim() ||
    ''
  );
}

/** UUID de solución desde la raíz del modelo Visita (listado o GET /visita/{id}). */
export function obtenerIdSolucionVisita(visita?: Visita | null): string | null {
  const id = visita?.id_solucion;
  if (typeof id !== 'string') return null;
  const trimmed = id.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Combina detalle GET y visita de listado cuando uno no trae id_solucion. */
export function resolverIdSolucionVisita(
  visita?: Visita | null,
  origen?: Visita | null
): string | null {
  return obtenerIdSolucionVisita(visita) ?? obtenerIdSolucionVisita(origen);
}

/** true cuando la visita está vinculada a una solución del plan de trabajo. */
export function visitaTieneSolucion(visita?: Visita | null): boolean {
  return obtenerIdSolucionVisita(visita) != null;
}

/** true cuando la visita tiene un registro parcial guardado en servidor (GET /visita/{id}). */
export function visitaEsRegistroParcial(visita?: Visita | null): boolean {
  if (!visita) return false;
  return visita.es_parcial === true || visita.registro_visita?.es_parcial === true;
}

/**
 * Completa la descripción en el detalle GET /visita/{id} cuando el listado sí la trae en registro_visita.
 */
export function complementarDescripcionVisita(
  detalle: Visita,
  origen?: Visita | null
): Visita {
  const id_solucion = resolverIdSolucionVisita(detalle, origen);
  const fecha_inicio_planificada =
    detalle.fecha_inicio_planificada?.trim() ||
    detalle.registro_visita?.fecha_inicio_planificada?.trim() ||
    origen?.fecha_inicio_planificada?.trim() ||
    origen?.registro_visita?.fecha_inicio_planificada?.trim() ||
    null;
  const fecha_termino_planificada =
    detalle.fecha_termino_planificada?.trim() ||
    detalle.registro_visita?.fecha_termino_planificada?.trim() ||
    origen?.fecha_termino_planificada?.trim() ||
    origen?.registro_visita?.fecha_termino_planificada?.trim() ||
    null;

  const aplicarCamposComplementarios = (v: Visita): Visita => {
    const regBase = v.registro_visita ?? {};
    const regOrigen = origen?.registro_visita;
    const documento_resumen = resolverDocumentoResumenVisita(v, origen);
    return normalizarCamposRegistroEnVisita({
      ...v,
      id_solucion,
      fecha_inicio_planificada,
      fecha_termino_planificada,
      es_parcial: v.es_parcial ?? origen?.es_parcial ?? regBase.es_parcial ?? regOrigen?.es_parcial ?? null,
      id_objetivo_visita:
        v.id_objetivo_visita ?? origen?.id_objetivo_visita ?? regOrigen?.id_objetivo_visita ?? null,
      id_modalidad_contacto:
        v.id_modalidad_contacto ??
        origen?.id_modalidad_contacto ??
        regOrigen?.id_modalidad_contacto ??
        null,
      modalidad_contacto:
        v.modalidad_contacto ?? origen?.modalidad_contacto ?? regOrigen?.modalidad_contacto ?? null,
      objetivo: v.objetivo ?? origen?.objetivo ?? regOrigen?.objetivo ?? null,
      objetivos: v.objetivos ?? origen?.objetivos ?? regOrigen?.objetivos ?? null,
      objetivo_otro: v.objetivo_otro ?? origen?.objetivo_otro ?? regOrigen?.objetivo_otro ?? null,
      tipo_reporte: v.tipo_reporte ?? origen?.tipo_reporte ?? regOrigen?.tipo_reporte ?? null,
      generar_folio: resolverGenerarFolioVisita(v, origen),
      folio: v.folio ?? origen?.folio ?? regOrigen?.folio ?? null,
      fecha_visita: v.fecha_visita ?? origen?.fecha_visita ?? regOrigen?.fecha_visita ?? null,
      hora_inicio: v.hora_inicio ?? origen?.hora_inicio ?? regOrigen?.hora_inicio ?? null,
      hora_termino: v.hora_termino ?? origen?.hora_termino ?? regOrigen?.hora_termino ?? null,
      recomendaciones: v.recomendaciones ?? origen?.recomendaciones ?? regOrigen?.recomendaciones ?? null,
      compromisos: v.compromisos ?? origen?.compromisos ?? regOrigen?.compromisos ?? null,
      documento_resumen: documento_resumen ?? null,
      adjuntos: deduplicarAdjuntosVisita(
        (v.adjuntos?.length ? v.adjuntos : null) ??
          (origen?.adjuntos?.length ? origen.adjuntos : null) ??
          (regBase.adjuntos?.length ? regBase.adjuntos : null) ??
          (regOrigen?.adjuntos?.length ? regOrigen.adjuntos : null) ??
          v.adjuntos ??
          origen?.adjuntos ??
          regBase.adjuntos ??
          regOrigen?.adjuntos ??
          undefined
      ),
      registro_visita: {
        ...regBase,
        ...(fecha_inicio_planificada ? { fecha_inicio_planificada } : {}),
        ...(fecha_termino_planificada ? { fecha_termino_planificada } : {}),
        ...(documento_resumen ? { documento_resumen } : {}),
      },
    });
  };

  if (!origen || obtenerDescripcionVisita(detalle)) {
    return aplicarCamposComplementarios(detalle);
  }
  const desc = obtenerDescripcionVisita(origen);
  if (!desc) {
    return aplicarCamposComplementarios(detalle);
  }
  const regBase = detalle.registro_visita ?? origen.registro_visita ?? {};
  return aplicarCamposComplementarios({
    ...detalle,
    descripcion: desc,
    registro_visita: { ...regBase, descripcion: desc },
  });
}

/** Respuesta cruda de la API (listado de visitas); items son VisitaResponse. */
interface VisitasPaginadasResponseRaw {
  items: VisitaResponse[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages: number | null;
}

/**
 * Interfaz para la respuesta paginada de visitas (items mapeados a Visita con lat/lng).
 * La API devuelve VisitaResponse[]; el servicio mapea a Visita[].
 */
export interface VisitasPaginadasResponse {
  items: Visita[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages: number | null;
}

/**
 * Servicio para gestionar visitas desde la API
 * 
 * NOTA: El token de autenticación se agrega automáticamente a todas las peticiones
 * mediante el AuthInterceptor. No es necesario agregar headers manualmente.
 */
@Injectable({
  providedIn: 'root'
})
export class VisitasService {
  private apiUrl = environment.apiUrl;
  private readonly http = inject(HttpClient);

  /** Misma petición compartida mientras hay suscriptores concurrentes (ej. tarjeta + mapa en /visitas). */
  private estadosVisitaCompartidos: Observable<EstadoVisita[]> | null = null;
  private tiposVisitaCompartidos: Observable<TipoVisita[]> | null = null;

  /**
   * Obtiene los estados de visita disponibles desde la API
   * @returns Observable con el array de estados de visita
   */
  obtenerEstadosVisita(): Observable<EstadoVisita[]> {
    if (!this.estadosVisitaCompartidos) {
      this.estadosVisitaCompartidos = this.http
        .get<EstadoVisita[]>(`${this.apiUrl}/visita/obtenerEstadosVisita`)
        .pipe(
          catchError(error => {
            console.error('Error al obtener estados de visita:', error);
            return of([]);
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
          finalize(() => {
            this.estadosVisitaCompartidos = null;
          })
        );
    }
    return this.estadosVisitaCompartidos;
  }

  /**
   * Obtiene los tipos de visita disponibles desde la API
   * @returns Observable con el array de tipos de visita
   */
  obtenerTiposVisita(): Observable<TipoVisita[]> {
    if (!this.tiposVisitaCompartidos) {
      this.tiposVisitaCompartidos = this.http
        .get<TipoVisita[]>(`${this.apiUrl}/visita/obtenerTiposVisita`)
        .pipe(
          catchError(error => {
            console.error('Error al obtener tipos de visita:', error);
            return of([]);
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
          finalize(() => {
            this.tiposVisitaCompartidos = null;
          })
        );
    }
    return this.tiposVisitaCompartidos;
  }

  /**
   * Obtiene las categorías de anexos para adjuntos de visitas.
   * OpenAPI: GET /api/v1/visita/obtenerCategoriasAnexo
   * @returns Observable con el array de categorías (id_categoria_anexo, descripcion)
   */
  obtenerCategoriasAnexo(): Observable<CategoriaAnexoResponse[]> {
    return this.http.get<CategoriaAnexoResponse[]>(
      `${this.apiUrl}/visita/obtenerCategoriasAnexo`
    ).pipe(
      catchError(error => {
        console.error('Error al obtener categorías de anexo:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene los objetivos de visita para el selector al registrar.
   * OpenAPI: GET /api/v1/visita/obtenerObjetivosVisita
   */
  obtenerObjetivosVisita(): Observable<ObjetivoVisita[]> {
    return this.http.get<ObjetivoVisita[]>(
      `${this.apiUrl}/visita/obtenerObjetivosVisita`
    ).pipe(
      catchError(error => {
        console.error('Error al obtener objetivos de visita:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene las modalidades de contacto para el selector al registrar.
   * OpenAPI: GET /api/v1/visita/obtenerModalidadContacto
   */
  obtenerModalidadContacto(): Observable<ModalidadContacto[]> {
    return this.http.get<ModalidadContacto[]>(
      `${this.apiUrl}/visita/obtenerModalidadContacto`
    ).pipe(
      catchError(error => {
        console.error('Error al obtener modalidades de contacto:', error);
        return of([]);
      })
    );
  }

  /**
   * Construye HttpParams a partir de VisitasQueryParams
   */
  private buildVisitasHttpParams(params: VisitasQueryParams): HttpParams {
    let httpParams = new HttpParams();
    if (params.desde) {
      httpParams = httpParams.set('desde', params.desde);
    }
    if (params.hasta) {
      httpParams = httpParams.set('hasta', params.hasta);
    }
    if (params.id_estado_visita !== undefined && params.id_estado_visita !== null) {
      httpParams = httpParams.set('id_estado_visita', params.id_estado_visita.toString());
    }
    if (params.id_tipo_visita !== undefined && params.id_tipo_visita !== null) {
      httpParams = httpParams.set('id_tipo_visita', params.id_tipo_visita.toString());
    }
    if (params.es_parcial !== undefined && params.es_parcial !== null) {
      httpParams = httpParams.set('es_parcial', params.es_parcial ? 'true' : 'false');
    }
    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    if (params.sort) {
      httpParams = httpParams.set('sort', params.sort);
    }
    if (params.order) {
      httpParams = httpParams.set('order', params.order);
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    return httpParams;
  }

  /**
   * Mapea la respuesta cruda de la API a VisitasPaginadasResponse con items como Visita (lat/lng)
   */
  private mapVisitasPaginadasResponse(respuesta: VisitasPaginadasResponseRaw): VisitasPaginadasResponse {
    const visitasMapeadas: Visita[] = (respuesta.items || []).map(visitaResp =>
      mapVisitaFromApiResponse(visitaResp)
    );
    return {
      items: visitasMapeadas,
      total: respuesta.total,
      page: respuesta.page,
      size: respuesta.size,
      pages: respuesta.pages
    };
  }

  /**
   * Obtiene el listado de visitas con filtros opcionales y paginación server-side.
   * NOTA OpenAPI: El spec en apip-sat no define GET /api/v1/visita/. El listado oficial es
   * GET /api/v1/visita/perfil/{id_perfil}/jerarquia/{id_jerarquia}. Preferir obtenerVisitasPorJerarquia
   * pasando id_perfil e id_jerarquia (id_jerarquia = -1 para todas las jerarquías).
   * @param params Parámetros de consulta para filtrar y paginar
   * @returns Observable con la respuesta paginada que incluye items, total, page, size, pages
   */
  obtenerVisitas(params: VisitasQueryParams = {}): Observable<VisitasPaginadasResponse> {
    const httpParams = this.buildVisitasHttpParams(params);
    const url = `${this.apiUrl}/visita/`;
    console.log('URL de visitas:', url, 'Parámetros:', httpParams.toString());
    return this.http.get<VisitasPaginadasResponseRaw>(url, { params: httpParams }).pipe(
      map(respuesta => {
        console.log('Respuesta paginada recibida:', {
          total: respuesta.total,
          page: respuesta.page,
          size: respuesta.size,
          pages: respuesta.pages,
          itemsCount: respuesta.items?.length || 0
        });
        return this.mapVisitasPaginadasResponse(respuesta);
      }),
      catchError(error => {
        console.error('Error al obtener visitas:', error);
        return of({
          items: [],
          total: 0,
          page: 1,
          size: params.size || 50,
          pages: 0
        });
      })
    );
  }

  /**
   * Obtiene el listado de visitas de un productor.
   * Endpoint OpenAPI: GET /api/v1/visita/productor/{id_productor}
   * @param id_productor UUID del productor
   * @param params Parámetros de consulta opcionales (desde, hasta, id_estado_visita, id_tipo_visita, sort, order, page, size, search)
   * @returns Observable con la respuesta paginada (items, total, page, size, pages)
   */
  obtenerVisitasPorProductor(
    id_productor: string,
    params: VisitasQueryParams = {}
  ): Observable<VisitasPaginadasResponse> {
    const httpParams = this.buildVisitasHttpParams(params);
    const url = `${this.apiUrl}/visita/productor/${encodeURIComponent(id_productor)}`;
    return this.http.get<VisitasPaginadasResponseRaw>(url, { params: httpParams }).pipe(
      map(respuesta => this.mapVisitasPaginadasResponse(respuesta)),
      catchError(error => {
        console.error('Error al obtener visitas por productor:', error);
        return of({
          items: [],
          total: 0,
          page: 1,
          size: params.size || 50,
          pages: 0
        });
      })
    );
  }

  /**
   * Obtiene el listado de visitas por perfil y jerarquía (unidad operativa).
   * Endpoint OpenAPI: GET /api/v1/visita/perfil/{id_perfil}/jerarquia/{id_jerarquia}
   * id_perfil e id_jerarquia son obligatorios en path. Use id_jerarquia = -1 para todas las jerarquías.
   * @param id_perfil ID del perfil (selector en header) — obligatorio
   * @param id_jerarquia ID de la jerarquía / unidad operativa (selector en header)
   * @param params Parámetros de consulta para filtrar y paginar
   * @returns Observable con la respuesta paginada
   */
  obtenerVisitasPorJerarquia(
    id_perfil: number,
    id_jerarquia: number,
    params: VisitasQueryParams = {},
    es_inicio: boolean = false
  ): Observable<VisitasPaginadasResponse> {
    const httpParams = this.buildVisitasHttpParams(params);
    let url = `${this.apiUrl}/visita/perfil/${id_perfil}/jerarquia/${id_jerarquia}`;
    if(es_inicio)
      url = `${this.apiUrl}/visita/perfil/${id_perfil}/jerarquia/${id_jerarquia}/proximasVisitas`;
    console.log('URL de visitas por jerarquía:', url, 'Parámetros:', httpParams.toString());
    return this.http.get<VisitasPaginadasResponseRaw>(url, { params: httpParams }).pipe(
      map(respuesta => {
        console.log('Respuesta paginada por jerarquía recibida:', {
          total: respuesta.total,
          page: respuesta.page,
          size: respuesta.size,
          pages: respuesta.pages,
          itemsCount: respuesta.items?.length || 0
        });
        return this.mapVisitasPaginadasResponse(respuesta);
      }),
      catchError(error => {
        console.error('Error al obtener visitas por jerarquía:', error);
        return of({
          items: [],
          total: 0,
          page: 1,
          size: params.size || 50,
          pages: 0
        });
      })
    );
  }

  /**
   * Obtiene el listado COMPLETO de visitas (sin paginación) para exportación.
   * @param id_perfil ID del perfil
   * @param id_jerarquia ID de la jerarquía
   * @param params Parámetros de consulta para filtrar
   * @returns Observable con el array de visitas completo
   */
  obtenerVisitasExport(
    id_perfil: number,
    id_jerarquia: number,
    params: VisitasQueryParams = {}
  ): Observable<Visita[]> {
    const httpParams = this.buildVisitasHttpParams(params);
    const url = `${this.apiUrl}/visita/perfil/${id_perfil}/jerarquia/${id_jerarquia}/export`;
    
    return this.http.get<VisitaResponse[]>(url, { params: httpParams }).pipe(
      map(items => (items || []).map(visitaResp => mapVisitaFromApiResponse(visitaResp))),
      catchError(error => {
        console.error('Error al obtener visitas para exportar:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene los detalles de una visita específica (GET /api/v1/visita/{id_visita}).
   * Incluye es_parcial y campos del borrador de registro cuando aplica.
   */
  obtenerDetallesVisita(idVisita: string): Observable<Visita> {
    return this.http.get<VisitaDetalleResponse>(
      `${this.apiUrl}/visita/${idVisita}`
    ).pipe(
      map(visitaResp => {
        const visita = mapVisitaFromApiResponse(visitaResp);
        const documento_resumen =
          visitaResp.documento_resumen ??
          visitaResp.registro_visita?.documento_resumen ??
          visita.documento_resumen ??
          visita.registro_visita?.documento_resumen ??
          null;
        const registro_visita = documento_resumen
          ? { ...(visita.registro_visita ?? {}), documento_resumen }
          : visita.registro_visita;
        return {
          ...visita,
          usuario: visitaResp.consultor?.nombre ?? visita.usuario ?? '',
          consultor: visitaResp.consultor ?? undefined,
          motivo: visitaResp.motivo ?? undefined,
          usuario_registro: visitaResp.usuario_registro ?? visita.usuario_registro ?? undefined,
          documento_resumen: documento_resumen ?? undefined,
          registro_visita,
          adjuntos: deduplicarAdjuntosVisita(
            visitaResp.adjuntos?.length
              ? visitaResp.adjuntos
              : visita.registro_visita?.adjuntos ?? visitaResp.adjuntos ?? undefined
          ),
          fecha_visita: visitaResp.fecha_visita ?? undefined,
          hora_inicio: visitaResp.hora_inicio ?? undefined,
          hora_termino: visitaResp.hora_termino ?? undefined,
          desarrollo_asesoria: visitaResp.desarrollo_asesoria ?? undefined,
          recomendaciones: visitaResp.recomendaciones ?? undefined,
          compromisos: visitaResp.compromisos ?? undefined,
          tipo_reporte: visitaResp.tipo_reporte ?? undefined,
          modalidad_contacto: visitaResp.modalidad_contacto ?? undefined,
          folio: visitaResp.folio ?? undefined,
          generar_folio:
            typeof visitaResp.generar_folio === 'boolean' ? visitaResp.generar_folio : false,
          id_solucion: visitaResp.id_solucion ?? visita.id_solucion ?? null,
          es_parcial: visitaResp.es_parcial ?? false,
        };
      }),
      map((visita) => normalizarCamposRegistroEnVisita(visita)),
      catchError(error => {
        console.error('Error al obtener detalles de visita:', error);
        throw error;
      })
    );
  }

  /**
   * Obtiene los detalles de una visita específica (GET /api/v1/visita/{id_visita}).
   * Incluye es_parcial y campos del borrador de registro cuando aplica.
   */
  obtenerVisitaDiagnosticoProd(idProductor: string): Observable<Visita | null> {
  return this.http.get<VisitaDetalleResponse[]>(
    `${this.apiUrl}/visita/productor/${idProductor}/visitaDiagnosticoActiva`
  ).pipe(
    map(visitaResp => {
      if (visitaResp.length > 0) {
        const visita = mapVisitaFromApiResponse(visitaResp[0]);
        return { ...visita };
      }
      return null;
    }),
    map(visita => {
      if (visita) {
        normalizarCamposRegistroEnVisita(visita);
        return visita; // 👈 devolver la visita
      }
      return null; // 👈 mantener el tipo consistente
    }),
    catchError(error => {
      console.error('Error al obtener detalles de visita:', error);
      throw error;
    })
  );
}

  /**
   * Construye la URL completa para descargar un adjunto (origen del apiUrl + url_descarga).
   * La descarga requiere autenticación; usar descargarAdjunto() para enviar el token.
   */
  getUrlDescargaCompleta(urlDescarga: string | null | undefined): string {
    if (!urlDescarga) return '';
    const base = new URL(this.apiUrl).origin;
    return urlDescarga.startsWith('/') ? `${base}${urlDescarga}` : `${base}/${urlDescarga}`;
  }

  /**
   * Descarga un adjunto de visita con el token de autenticación.
   * @param urlDescarga Valor adjunto.url_descarga (path relativo, ej. /api/v1/visita/.../adjunto/archivo.jpg)
   * @returns Observable con el blob del archivo
   */
  descargarAdjunto(urlDescarga: string | null | undefined): Observable<Blob> {
    const url = this.getUrlDescargaCompleta(urlDescarga);
    if (!url) {
      return throwError(() => new Error('URL de descarga no disponible'));
    }
    return this.http.get(url, { responseType: 'blob' }).pipe(
      catchError(error => {
        console.error('Error al descargar adjunto:', error);
        throw error;
      })
    );
  }

  /**
   * Crea una nueva visita (POST /api/v1/visita/crear).
   * Body: application/json según CrearVisitaRequest (id_productor, fecha, lugar, id_tipo_visita, id_objetivo_visita opcional, comentarios, es_contingencia, bolsa_visita).
   * @param datosVisita Payload validado según CrearVisitaRequestSchema
   * @returns Observable con la respuesta del servidor
   */
  crearVisita(datosVisita: CrearVisitaRequestValidated): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/visita/crear`,
      datosVisita
    ).pipe(
      catchError(error => {
        console.error('Error al crear visita:', error);
        throw error;
      })
    );
  }

  /**
   * Reagenda una visita existente (POST /api/v1/visita/{id_visita}/agendar).
   * Body: AgendarVisitaRequest (fecha, lugar, id_tipo_visita, id_objetivo_visita opcional, es_contingencia, bolsa_visita, descripcion, …).
   * @param idVisita ID de la visita a reagendar
   * @param datosVisita Payload validado según AgendarVisitaRequestSchema
   * @returns Observable con la respuesta
   */
  reagendarVisita(idVisita: string, datosVisita: AgendarVisitaRequestValidated): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/visita/${idVisita}/agendar`,
      datosVisita
    ).pipe(
      catchError(error => {
        console.error('Error al reagendar visita:', error);
        throw error;
      })
    );
  }

  /**
   * Cancela una visita
   * @param idVisita ID de la visita a cancelar
   * @returns Observable con la respuesta
   */
  cancelarVisita(idVisita: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/visita/${idVisita}/cancelar`,
      {}
    ).pipe(
      catchError(error => {
        console.error('Error al cancelar visita:', error);
        throw error;
      })
    );
  }

  /**
   * Registra una visita (POST /api/v1/visita/{id_visita}/registrar, multipart/form-data).
   * @param idVisita ID de la visita a registrar
   * @param formData Campos multipart OpenAPI: es_parcial, fecha_visita, hora_inicio, hora_termino,
   *   tipo_reporte, id_objetivo_visita (repetir por cada id), id_modalidad_contacto, generar_folio,
   *   folio, recomendaciones, compromisos, desarrollo_asesoria, es_contingencia,
   *   descripcion_emergencia, files, id_categorias
   * Con `generar_folio=true` el backend asigna el folio y no debe enviarse `folio` en el multipart.
   * Con `es_parcial=false` y reporte papel el backend exige `files`; al persistir debe reemplazar
   * adjuntos previos del borrador (no append), para evitar duplicados si el cliente reenvía los mismos archivos.
   * @returns Observable con la respuesta del POST (estado, data: VisitaData, error)
   */
  registrarVisita(idVisita: string, formData: FormData): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/visita/${idVisita}/registrar`,
      formData
    ).pipe(
      catchError(error => {
        console.error('Error al registrar visita:', error);
        throw error;
      })
    );
  }

  /**
   * Sube o reemplaza el resumen firmado de una visita.
   * OpenAPI: PUT /api/v1/visita/{id_visita}/guardarResumenVisita
   * Body: multipart `documento_resumen`
   */
  guardarResumenVisita(idVisita: string, archivo: File): Observable<boolean> {
    const form = new FormData();
    form.append('documento_resumen', archivo, archivo.name);
    return this.http.put<boolean>(
      `${this.apiUrl}/visita/${idVisita}/guardarResumenVisita`,
      form
    ).pipe(
      catchError(error => {
        console.error('Error al guardar resumen firmado:', error);
        throw error;
      })
    );
  }

  /**
   * Elimina el resumen firmado de una visita.
   * OpenAPI: PUT /api/v1/visita/{id_visita}/eliminarResumenVisita
   */
  eliminarResumenVisita(idVisita: string): Observable<boolean> {
    return this.http.put<boolean>(
      `${this.apiUrl}/visita/${idVisita}/eliminarResumenVisita`,
      null
    ).pipe(
      catchError(error => {
        console.error('Error al eliminar resumen firmado:', error);
        throw error;
      })
    );
  }

}
